import { Alert, Badge, Table } from "@xola/ui-kit";
import { getSellerLocale, titleCase } from "common";
import React, { forwardRef, useMemo } from "react";
import { useMutation } from "react-query";
import { Virtuoso } from "react-virtuoso";
import { Translation } from "types";
import { api } from "@/api/api";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader } from "@/components/Loader";
import { Language } from "@/components/Settings/Languages/LanguagesDropdown";
import { TranslationsApiFilters } from "@/components/Settings/Translations/TranslationsFilter";
import { useSeller } from "@/hooks/useAccount";
import { useCollection } from "@/hooks/useCollection";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { useLocalizationStore } from "@/stores/localizationStore";
import { updateCache } from "@/utils/updateCache";

interface TranslationsTableProps {
    locale: string;
    height: number;
    filters: TranslationsApiFilters;
}

const OUTER_HEIGHT = 140; // Table header height and Padding

const HTML_FIELDS = {
    reviewTemplate: ["App\\Document\\Preference\\ReviewPreference"],
    emailFooter: ["App\\Document\\Seller"],
};

export const TranslationsTable = ({ height, filters, locale }: TranslationsTableProps) => {
    const queryParams = useMemo(() => {
        const params: Record<string, any> = {
            limit: 100,
            seller: filters.seller,
            locale: filters.locale,
        };

        if (filters.ancestors.length > 0) {
            params.ancestors = filters.ancestors.join(",");
        }

        if (filters.tag.length > 0) {
            params["tags._id"] = filters.tag.join(",");
        }

        return params;
    }, [filters]);

    const {
        data: translations,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteCollection<Translation>(["translations"], queryParams, {
        cacheTime: 0,
    });

    const scrollableContainerHeight = height - OUTER_HEIGHT;

    if (isFetching && translations.length === 0) {
        return <Loader />;
    }

    if (translations.length === 0 && filters.tag.length === 0 && filters.ancestors.length === 0) {
        return (
            <Alert color="warning" className="text-center">
                We are still generating the translations for this language. Please try again in a minute or two.
            </Alert>
        );
    }

    if (isError) {
        return <Alert color="danger">An error occurred while fetching translation data </Alert>;
    }

    return (
        <div className="rounded-lg border border-gray-lighter">
            <Table>
                <TranslationsTableHeader locale={locale} />
            </Table>
            {translations.length > 0 ? (
                <Virtuoso
                    style={{ height: scrollableContainerHeight }}
                    className="divide-y divide-gray-lighter"
                    data={translations}
                    itemContent={(_, translation) => <TranslationRow key={translation.id} translation={translation} />}
                    endReached={async () => {
                        if (hasNextPage) {
                            await fetchNextPage();
                        }
                    }}
                    components={{
                        Footer: () => (isFetchingNextPage ? <Loader /> : null),
                        List: forwardRef((props, ref) => (
                            <div {...props} ref={ref} className="divide-y divide-gray-lighter" />
                        )),
                    }}
                />
            ) : (
                <div className="p-4 text-center">No translations found with applied filters</div>
            )}
        </div>
    );
};

interface TranslationsTableHeaderProps {
    locale: string;
}

const TranslationsTableHeader = ({ locale }: TranslationsTableHeaderProps) => {
    const seller = useSeller();
    const accountLocale = getSellerLocale(seller);
    const localizeLanguages = useLocalizationStore().availableLanguages;
    const accountLanguage = localizeLanguages.find((language) => language.code === accountLocale);

    const { data: languages } = useCollection<Language, "locale">(["languages"]);
    const translationLanguage = languages.find((language) => language.locale === locale);

    if (!accountLanguage || !translationLanguage) {
        return null;
    }

    return (
        <Table.Head>
            <Table.Row className="flex">
                <Table.Header className="w-1/3">Context</Table.Header>
                <Table.Header className="w-1/3">Account Language ({accountLanguage.name})</Table.Header>
                <Table.Header className="w-1/3">Translation ({translationLanguage.language})</Table.Header>
            </Table.Row>
        </Table.Head>
    );
};

interface TranslationRowProps {
    translation: Translation;
}

const TranslationRow = ({ translation }: TranslationRowProps) => {
    const isHtml =
        translation.field in HTML_FIELDS
            ? HTML_FIELDS[translation.field as keyof typeof HTML_FIELDS].includes(translation.objectClass)
            : false;

    const renderContent = (text?: Translation["contentOriginal"]) => {
        if (isHtml && text) {
            // eslint-disable-next-line react/no-danger
            return <div dangerouslySetInnerHTML={{ __html: text as string }} />;
        }

        if (Array.isArray(text)) {
            return text.join("\n");
        }

        return text ?? <span className="text-gray">Not Available</span>;
    };

    return (
        <ErrorBoundary fallbackMessage={`Error rendering translation ${translation.id}`}>
            <Table.Row className="flex">
                <Table.Cell className="flex w-1/3 items-center !whitespace-normal py-3">
                    <BreadcrumbCell translation={translation} />
                </Table.Cell>

                <Table.Cell className="flex w-1/3 items-center overflow-hidden !whitespace-pre-wrap">
                    {renderContent(translation.contentOriginal)}
                </Table.Cell>

                {isHtml ? (
                    <Table.Cell className="flex w-1/3 items-center overflow-hidden !whitespace-pre-wrap">
                        {renderContent(translation.content ?? translation.contentOriginal)}
                    </Table.Cell>
                ) : (
                    <EditableTranslationCell translation={translation} />
                )}
            </Table.Row>
        </ErrorBoundary>
    );
};

interface BreadcrumbCellProps {
    translation: Translation;
}

const BreadcrumbCell = ({ translation }: BreadcrumbCellProps) => {
    const breadcrumbParts = translation.breadcrumb?.split(" > ") ?? [];
    const lastPart = breadcrumbParts.pop();
    const previousParts = breadcrumbParts.join(" > ");

    return (
        <div className="space-y-2">
            <span>
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: previousParts + (lastPart ? " > " : "") }}
                />
                <strong>{lastPart}</strong>
            </span>
            <div className="space-x-2">
                <Badge color={translation.product ? "warning" : "success"} size="small">
                    {translation.product ? titleCase(translation.product.object) : "Global Settings"}
                </Badge>
                {translation.product && <Badge>{translation.product.name}</Badge>}
            </div>
        </div>
    );
};

interface EditableTranslationCellProps {
    translation: Translation;
}

const EditableTranslationCell = ({ translation }: EditableTranslationCellProps) => {
    const translationText: string = useMemo(() => {
        if (Array.isArray(translation.contentOriginal)) {
            const contentArray = translation.content ? JSON.parse(translation.content) : translation.contentOriginal;
            return contentArray.join("\n");
        }

        return translation.content ?? translation.contentOriginal;
    }, [translation.contentOriginal, translation.content]);

    const { mutateAsync } = useMutation(async (newContent: string | string[]) => {
        return api.put<Translation>(`/translations/${translation.id}`, { content: newContent });
    });

    const handleSave = async (updatedContent: string) => {
        try {
            const payloadContent = Array.isArray(translation.contentOriginal)
                ? JSON.stringify(updatedContent.split("\n"))
                : updatedContent;
            const { data } = await mutateAsync(payloadContent);
            updateCache("translations", data, false);
        } catch {}
    };

    return (
        <Table.EditableCell
            className="flex w-1/3 items-center overflow-hidden !whitespace-normal"
            value={translationText}
            onSave={handleSave}
        />
    );
};
